import { CreditCard, Home, KeyboardArrowDown, Language, Logout, SpaceDashboard } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Avatar, Backdrop, Box, CircularProgress, Container, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { assets } from "../../assets";
import { useModals } from "../../context/ModalContext";
import { appStates, setAppState } from "../../redux/features/appStateSlice";
import { resetConnectBusinessState } from "../../redux/features/connectBusinessStateSlice";
import { resetMarkerCount } from "../../redux/features/coverageSlice";
import { setPlanSelected } from "../../redux/features/planStateSlice";
import { selectCurrentCompany, selectRole, selectUserCompanies } from "../../redux/features/user/selectors/userState.selectors";
import { resetUser, updateUser } from "../../redux/features/userStateSlice";
import { disconnectWithServer } from "../../sockets";
import { IRoles } from "../../utils";
import { ConnectBusinessAccount } from "../ConnectBusinessAccount";
import { ConnectBusinessExit } from "../ConnectBusinessExit";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { CompanyTime } from "../common/CompanyTime";
import { Notification } from "../notifications/Notification";
import { ReleaseBanner } from "@features/Releases";
import { useSwitchActiveCompany } from "../shared/hooks/useSwitchActiveCompany";
import { colors, avatarColorOptions, alphaVar } from "../ui/colors";
import { performSignOut } from "../../utils/performSignOut";
import { ThemePicker } from "../../theme";

export const appBarHeight = 64;

const ALLOWED_ROLES = new Set([IRoles.Employee, IRoles.Business, IRoles.SuperAdmin, IRoles.Admin, IRoles.Manager, IRoles.VR, IRoles.Other]);

const LOGO_TITLE = "Flownect";
const LOGO_ICON = assets.images.flownecticon;
const BLUE_GRADIENT = `linear-gradient(135deg, ${colors.blue[200]} 0%, ${colors.blue[400]} 50%, ${colors.blue[500]} 100%)`;

function ResponsiveAppBar({ basePath }) {
  const companies = useSelector(selectUserCompanies);
  const currentCompany = useSelector(selectCurrentCompany);
  const roleType = useSelector(selectRole);
  const activeCompanyId = useSelector((state) => state.userState.activeCompanyId);
  const userState = useSelector((state) => state.userState);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { closeAllModals, openModal } = useModals();
  const { switchActiveCompany, isSwitchingCompany, switchingCompanyId } = useSwitchActiveCompany();

  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasCompanies = (companies ?? []).length > 0;

  const userShortName = `${userState?.firstName?.split(" ")[0] ?? ""} ${userState?.lastName?.slice(0, 1) ?? ""}.`.trim();
  const topLabel = currentCompany?.businessName ?? userShortName;
  const subLabel = hasCompanies ? userShortName : (userState?.roles?.[0]?.name ?? "");

  const token = sessionStorage.getItem("token");

  if (!token || location.pathname === "/signin") return null;
  if (!ALLOWED_ROLES.has(roleType)) return null;

  const handleOpenNavMenu = (e) => setAnchorElNav(e.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleOpenModalAccount = () => openModal("Dashboard-Account-Settings");

  const handleResetNeededState = () => {
    resetConnectBusinessState();
    dispatch(resetMarkerCount());
    dispatch(setPlanSelected({ upgradeFreeTrial: false }));
  };

  const handleSignOff = async () => {
    await performSignOut(currentCompany?.id);
    dispatch(resetUser());
    disconnectWithServer();
    navigate("/flow-hub");
  };

  const handleNavTo = (path, extraAction) => {
    navigate(path);
    handleResetNeededState();
    if (extraAction) dispatch(extraAction);
  };

  const onSelectCompany = async (selectedCompanyId) => {
    try {
      handleCloseNavMenu();
      await switchActiveCompany(selectedCompanyId);
    } catch (error) {
      console.error("Error switching company:", error);
    }
  };

  const navItems = [
    {
      name: "Dashboard",
      icon: <SpaceDashboard />,
      path: basePath === "/" ? "/business" : basePath,
      onClick: () => handleNavTo(basePath === "/" ? "/business" : basePath, setAppState({ currState: appStates.HOME })),
    },
    {
      name: "Organization",
      icon: <Language />,
      path: `${basePath}/organization`,
      onClick: () => handleNavTo(`${basePath}/organization`),
    },
    {
      name: "Subscription",
      icon: <CreditCard />,
      path: `${basePath}/plan-center`,
      onClick: () => handleNavTo(`${basePath}/plan-center`, setAppState({ currState: appStates.PLANS })),
    },
    {
      name: "Apps Center",
      icon: <Home />,
      path: `${basePath}/apps-center`,
      onClick: () => handleNavTo(`${basePath}/apps-center`),
    },
  ];

  //bloque avatar  letra inicial mas color random consistente
  const firstNametoNumber = (firstName) => {
    return firstName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  };

  const numberToColor = (number) => {
    return avatarColorOptions[number % avatarColorOptions.length];
  };
  const avatarInit = !userState?.avatar && userState?.firstName ? userState.firstName.charAt(0).toUpperCase() : "";
  const hasAvatar = Boolean(userState?.avatar);
  const avatarRandomColor = !hasAvatar && userState?.firstName ? numberToColor(firstNametoNumber(userState.firstName)) : colors.gray[300];
  const fullNameDisplay = `${userState?.firstName ?? ""} ${userState?.lastName ?? ""}`.trim();
  const aliasDisplay = userState?.alias?.trim();
  const accountDisplayName = userState?.useAliasAsDisplayName ? aliasDisplay || fullNameDisplay : fullNameDisplay || aliasDisplay;

  // traer info de userState para que se muestre en un tooltip al hacer hoover en el avatar
  const tooltipInfo = (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: -2 }}>
      <Typography variant="body2" fontWeight={700}>
        User account
      </Typography>
      <Typography variant="body3">{accountDisplayName}</Typography>
      <Typography variant="body3">{userState?.email ?? ""}</Typography>
    </Box>
  );

  return createPortal(
    <>
      <AppBar
        position="fixed"
        top="0"
        sx={{
          zIndex: 2,
          width: "100%",
          minWidth: 0,
          height: `${appBarHeight}px`,
          boxShadow: "unset",
          color: "text.primary",
          backgroundColor: colors.warmWhite.base,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth={false} sx={{ zIndex: 1, width: "100%" }}>
          <Toolbar disableGutters sx={{ minHeight: `${appBarHeight}px`, px: 0 }}>
            {/* Desktop logo */}
            <IconButton edge="start" onClick={() => navigate(basePath)}>
              <Avatar src={LOGO_ICON} sx={{ display: { xs: "none", md: "flex" } }} />
            </IconButton>
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 2 }}>
              <Typography
                variant="h6"
                noWrap
                component="a"
                href="/"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: ".05rem",
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                {LOGO_TITLE}
              </Typography>
            </Box>

            {/* Mobile nav */}
            <Box sx={{ flexGrow: 3, display: { xs: "flex", md: "none" }, pl: 0, ml: 0 }}>
              <IconButton edge="start" onClick={handleOpenNavMenu} color="inherit">
                <MenuIcon sx={{ fontSize: 30 }} />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{ display: { xs: "block", md: "none" }, zIndex: 2147483647 }}
              >
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <MenuItem
                      key={`xs-${index}`}
                      onClick={() => {
                        closeAllModals();
                        item.onClick();
                        handleCloseNavMenu();
                      }}
                      sx={{
                        color: isActive ? "#FFFFFF" : "text.primary",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: isActive ? BLUE_GRADIENT : "inherit",
                      }}
                    >
                      <Box sx={{ display: "inline-flex", mr: 0.5 }}>{item.icon}</Box>
                      <Typography>{item.name}</Typography>
                    </MenuItem>
                  );
                })}

                {(companies ?? []).map((membership) => {
                  const isActive = String(membership.companyId) === String(activeCompanyId);
                  const isLoading = isSwitchingCompany && String(switchingCompanyId) === String(membership.companyId);
                  return (
                    <MenuItem
                      key={membership.companyId}
                      disabled={isActive || isSwitchingCompany}
                      onClick={() => onSelectCompany(membership.companyId)}
                    >
                      <Typography>{membership.company?.businessName}</Typography>
                      {isLoading ? <CircularProgress size={16} /> : isActive ? <Box /> : null}
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>

            {/* Mobile logo */}
            <Avatar src={LOGO_ICON} sx={{ display: { xs: "flex", md: "none" }, mr: { xs: -0.7, sm: 1 }, width: { xs: "35px" }, height: "auto" }} />
            <Typography
              variant="h5"
              noWrap
              component="a"
              href="/"
              sx={{
                display: { xs: "flex", md: "none" },
                flexGrow: 1,
                fontFamily: "monospace",
                fontWeight: 700,
                color: "inherit",
                textDecoration: "none",
                fontSize: { sm: "1rem", xs: ".8rem" },
                letterSpacing: { sm: ".1rem", xs: "0rem" },
              }}
            >
              {LOGO_TITLE}
            </Typography>

            {/* Right-side actions */}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
              <ReleaseBanner placement="right" />
              <CompanyTime />
              <ThemePicker />
              <Notification />

              {/* User menu */}
              {userState?.firstName?.length > 0 && (
                <Box sx={{ position: "relative" }}>
                  <Tooltip title={tooltipInfo} sx={{ backgroundColor: "transparent" }}>
                    <IconButton edge="end" onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar alt="User" src={userState.avatar ?? undefined} sx={{ bgcolor: avatarRandomColor }}>
                        {avatarInit}
                      </Avatar>
                    </IconButton>
                  </Tooltip>

                  <Menu
                    id="user-menu"
                    anchorEl={anchorElUser}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{ zIndex: 2147483647, mt: 6 }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCloseUserMenu();
                        handleOpenModalAccount();
                      }}
                      sx={{ color: "text.primary" }}
                    >
                      <Typography textAlign="center">Account</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleSignOff();
                        handleCloseUserMenu();
                      }}
                      sx={{ color: colors.danger[1000] }}
                    >
                      <Box sx={{ mr: 1 }}>
                        <Logout sx={{ color: colors.danger[1000] }} />
                      </Box>
                      <Typography textAlign="center">Log out</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <BaseModalComponent defaultSizes={{ width: 700 }} modalLabel="My account" modalKey="Dashboard-Account-Settings">
        <ConnectBusinessAccount componentState={userState} updateComponentState={updateUser} />
      </BaseModalComponent>

      <ConnectBusinessExit open={false} setOpen={() => {}} continueAction={null} />

      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 100,
          color: "text.primary",
          backgroundColor: (theme) => alphaVar(theme.palette.background.default, 0.7),
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={isSwitchingCompany}
      >
        <CircularProgress color="primary" />
        <Typography sx={{ color: colors.gray.base, fontWeight: 600 }}>Changing...</Typography>
      </Backdrop>
    </>,
    document.body,
  );
}

export default ResponsiveAppBar;
